// src/pages/Settings.js
import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FaTrash, FaStar, FaBuilding, FaImage, FaQuoteRight, FaExchangeAlt, FaTags, FaPhone, FaPercent, FaTruck, FaInstagram, FaFacebook, FaWhatsapp, FaEnvelope, FaPlus, FaPen, FaTimes } from 'react-icons/fa';

// Section Card Component - defined outside to prevent re-renders
const SectionCard = ({ icon: Icon, title, children, iconBg = 'bg-primary-50', iconColor = 'text-primary-600' }) => (
  <section className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden">
    <div className="p-6 border-b border-slate-100">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 ${iconBg} rounded-xl flex items-center justify-center`}>
          <Icon className={`${iconColor} text-lg`} />
        </div>
        <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
      </div>
    </div>
    <div className="p-6">
      {children}
    </div>
  </section>
);

// Submit Button Component - defined outside to prevent re-renders
const SubmitButton = ({ children }) => (
  <button
    type="submit"
    className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3 rounded-xl font-semibold hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-500/30 flex items-center justify-center gap-2"
  >
    {children}
  </button>
);

const Settings = () => {
  const [companyInfo, setCompanyInfo] = useState({ companyName: '', logoFile: null, currentLogo: '' });
  const [adPanels, setAdPanels] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [transformations, setTransformations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [contactDetails, setContactDetails] = useState({ phone: '', email: '', instagram: '', facebook: '', whatsapp: '' });
  const [taxDetails, setTaxDetails] = useState({ gstRate: '', gstNumber: '' });
  const [deliverySettings, setDeliverySettings] = useState({ freeDeliveryThreshold: '', deliveryCharge: '' });

  const [adPanelForm, setAdPanelForm] = useState({ backgroundImage: null, mainText: '', smallText: '' });
  const [testimonialForm, setTestimonialForm] = useState({ stars: 5, description: '', customerName: '' });
  const emptyTransformationForm = { productName: '', before: '', after: '', beforeImage: null, afterImage: null };
  const [transformationForm, setTransformationForm] = useState(emptyTransformationForm);
  // Edit mode: id of the transformation being edited (null = creating a new one),
  // plus the existing image filenames so we can show/keep/remove them.
  const [editingTransformationId, setEditingTransformationId] = useState(null);
  const [currentTransformationImages, setCurrentTransformationImages] = useState({ before: '', after: '' });
  const transformationFormRef = useRef(null);
  const [categoryForm, setCategoryForm] = useState({ categoryName: '' });

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      // Fetch company name
      const nameRes = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/companyName`);
      if (nameRes.ok) {
        const name = await nameRes.json();
        setCompanyInfo(prev => ({ ...prev, companyName: name }));
      }

      // Fetch company logo
      const logoRes = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/companyLogo`);
      if (logoRes.ok) {
        const logo = await logoRes.json();
        setCompanyInfo(prev => ({ ...prev, currentLogo: logo }));
      }

      // Fetch ad panels
      const adRes = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/adPanels`);
      if (adRes.ok) {
        const ads = await adRes.json();
        setAdPanels(ads || []);
      }

      // Fetch testimonials
      const testRes = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/testimonials`);
      if (testRes.ok) {
        const tests = await testRes.json();
        setTestimonials(tests || []);
      }

      // Fetch transformations
      const transRes = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/transformations`);
      if (transRes.ok) {
        const trans = await transRes.json();
        setTransformations(trans || []);
      }

      // Fetch categories
      const catRes = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/categories/all`);
      if (catRes.ok) {
        const cats = await catRes.json();
        setCategories(cats || []);
      }

      // Fetch contact details
      const contactRes = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/contactDetails`);
      if (contactRes.ok) {
        const contact = await contactRes.json();
        setContactDetails(contact || { phone: '', email: '', instagram: '', facebook: '', whatsapp: '' });
      }

      // Fetch tax details
      const taxRes = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/taxDetails`);
      if (taxRes.ok) {
        const tax = await taxRes.json();
        setTaxDetails(tax || { gstRate: '', gstNumber: '' });
      }

      // Fetch delivery settings
      const deliveryRes = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/deliverySettings`);
      if (deliveryRes.ok) {
        const delivery = await deliveryRes.json();
        setDeliverySettings(delivery || { freeDeliveryThreshold: '', deliveryCharge: '' });
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  // Company Info Handlers
  const handleCompanyInfoSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('companyName', companyInfo.companyName);
      if (companyInfo.logoFile) {
        formData.append('logo', companyInfo.logoFile);
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/company-info`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) throw new Error('Failed to update company info');
      toast.success('Company info updated successfully');
      fetchAllSettings();
      setCompanyInfo(prev => ({ ...prev, logoFile: null }));
    } catch (err) {
      toast.error('Failed to update company info');
    }
  };

  // Ad Panel Handlers
  const handleAdPanelSubmit = async (e) => {
    e.preventDefault();
    if (!adPanelForm.backgroundImage) {
      toast.error('Please select a background image');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('backgroundImage', adPanelForm.backgroundImage);
      formData.append('mainText', adPanelForm.mainText);
      formData.append('smallText', adPanelForm.smallText);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/ad-panels`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) throw new Error('Failed to create ad panel');
      const data = await res.json();
      setAdPanels(data.adPanels);
      toast.success('Ad panel created successfully');
      setAdPanelForm({ backgroundImage: null, mainText: '', smallText: '' });
      document.getElementById('adPanelImageInput').value = '';
    } catch (err) {
      toast.error('Failed to create ad panel');
    }
  };

  const handleDeleteAdPanel = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ad panel?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/ad-panels/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to delete ad panel');
      const data = await res.json();
      setAdPanels(data.adPanels);
      toast.success('Ad panel deleted successfully');
    } catch (err) {
      toast.error('Failed to delete ad panel');
    }
  };

  // Testimonial Handlers
  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/testimonials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(testimonialForm),
      });

      if (!res.ok) throw new Error('Failed to create testimonial');
      const data = await res.json();
      setTestimonials(data.testimonials);
      toast.success('Testimonial created successfully');
      setTestimonialForm({ stars: 5, description: '', customerName: '' });
    } catch (err) {
      toast.error('Failed to create testimonial');
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/testimonials/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to delete testimonial');
      const data = await res.json();
      setTestimonials(data.testimonials);
      toast.success('Testimonial deleted successfully');
    } catch (err) {
      toast.error('Failed to delete testimonial');
    }
  };

  // Transformation Handlers
  const resetTransformationForm = () => {
    setTransformationForm(emptyTransformationForm);
    setEditingTransformationId(null);
    setCurrentTransformationImages({ before: '', after: '' });
  };

  const handleEditTransformation = (trans) => {
    setEditingTransformationId(trans.id);
    setTransformationForm({
      productName: trans.productName || '',
      before: trans.before || '',
      after: trans.after || '',
      beforeImage: null,
      afterImage: null,
      removeBeforeImage: false,
      removeAfterImage: false,
    });
    setCurrentTransformationImages({ before: trans.beforeImage || '', after: trans.afterImage || '' });
    // Bring the transformation form (not the page top) into view so the admin
    // sees what they're editing right away.
    requestAnimationFrame(() => {
      transformationFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleTransformationSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('productName', transformationForm.productName);
      formData.append('before', transformationForm.before);
      formData.append('after', transformationForm.after);
      if (transformationForm.beforeImage) formData.append('beforeImage', transformationForm.beforeImage);
      if (transformationForm.afterImage) formData.append('afterImage', transformationForm.afterImage);

      const isEdit = !!editingTransformationId;
      if (isEdit) {
        if (transformationForm.removeBeforeImage) formData.append('removeBeforeImage', 'true');
        if (transformationForm.removeAfterImage) formData.append('removeAfterImage', 'true');
      }

      const url = isEdit
        ? `${process.env.REACT_APP_API_URL}/api/settings/transformations/${editingTransformationId}`
        : `${process.env.REACT_APP_API_URL}/api/settings/transformations`;

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) throw new Error('Failed to save transformation');
      const data = await res.json();
      setTransformations(data.transformations);
      toast.success(isEdit ? 'Transformation updated successfully' : 'Transformation created successfully');
      resetTransformationForm();
    } catch (err) {
      toast.error('Failed to save transformation');
    }
  };

  const handleDeleteTransformation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transformation?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/transformations/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to delete transformation');
      const data = await res.json();
      setTransformations(data.transformations);
      toast.success('Transformation deleted successfully');
    } catch (err) {
      toast.error('Failed to delete transformation');
    }
  };

  // Category Handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryForm.categoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ categoryName: categoryForm.categoryName }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to create category');
      }

      const data = await res.json();
      setCategories(data.categories);
      toast.success('Category created successfully');
      setCategoryForm({ categoryName: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to create category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings/categories/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to delete category');
      const data = await res.json();
      setCategories(data.categories);
      toast.success('Category deleted successfully');
    } catch (err) {
      toast.error('Failed to delete category');
    }
  };

  // Contact Details Handlers
  const handleContactDetailsSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ key: 'contactDetails', value: contactDetails }),
      });

      if (!res.ok) throw new Error('Failed to update contact details');
      toast.success('Contact details updated successfully');
    } catch (err) {
      toast.error('Failed to update contact details');
    }
  };

  // Tax Details Handlers
  const handleTaxDetailsSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ key: 'taxDetails', value: taxDetails }),
      });

      if (!res.ok) throw new Error('Failed to update tax details');
      toast.success('Tax details updated successfully');
    } catch (err) {
      toast.error('Failed to update tax details');
    }
  };

  // Delivery Settings Handlers
  const handleDeliverySettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ key: 'deliverySettings', value: deliverySettings }),
      });

      if (!res.ok) throw new Error('Failed to update delivery settings');
      toast.success('Delivery settings updated successfully');
    } catch (err) {
      toast.error('Failed to update delivery settings');
    }
  };

  // Common input class
  const inputClass = "w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all";

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-2"
      >
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your store configuration</p>
      </motion.div>

      {/* Company Info Section */}
      <SectionCard icon={FaBuilding} title="Company Information">
        <form onSubmit={handleCompanyInfoSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Name</label>
            <input
              type="text"
              value={companyInfo.companyName}
              onChange={(e) => setCompanyInfo({ ...companyInfo, companyName: e.target.value })}
              placeholder="Enter company name"
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Logo</label>
            {companyInfo.currentLogo && (
              <div className="mb-3 p-3 bg-slate-50 rounded-xl inline-block">
                <img
                  src={`${process.env.REACT_APP_API_URL}/api/images/${companyInfo.currentLogo}`}
                  alt="Current Logo"
                  className="h-16 object-contain"
                />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCompanyInfo({ ...companyInfo, logoFile: e.target.files[0] })}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-600 hover:file:bg-primary-100"
            />
          </div>
          <SubmitButton>Update Company Info</SubmitButton>
        </form>
      </SectionCard>

      {/* Ad Panels Section */}
      <SectionCard icon={FaImage} title="Ad Panels (Top Banner)" iconBg="bg-purple-50" iconColor="text-purple-600">
        <form onSubmit={handleAdPanelSubmit} className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Background Image</label>
            <input
              id="adPanelImageInput"
              type="file"
              accept="image/*"
              onChange={(e) => setAdPanelForm({ ...adPanelForm, backgroundImage: e.target.files[0] })}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-600 hover:file:bg-purple-100"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Main Text (Headline)</label>
            <input
              type="text"
              value={adPanelForm.mainText}
              onChange={(e) => setAdPanelForm({ ...adPanelForm, mainText: e.target.value })}
              placeholder="e.g., Handcrafted Crochet with Love"
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Small Text (Subtitle)</label>
            <input
              type="text"
              value={adPanelForm.smallText}
              onChange={(e) => setAdPanelForm({ ...adPanelForm, smallText: e.target.value })}
              placeholder="e.g., Premium artisan pieces woven with passion"
              required
              className={inputClass}
            />
          </div>
          <SubmitButton><FaPlus className="text-sm" /> Add Ad Panel</SubmitButton>
        </form>

        <div className="space-y-3">
          <h3 className="text-sm font-medium text-slate-700">Current Ad Panels ({adPanels.length})</h3>
          {adPanels.map((panel) => (
            <div key={panel.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
              <img
                src={`${process.env.REACT_APP_API_URL}/api/images/${panel.backgroundImage}`}
                alt="Ad"
                className="w-24 h-16 object-cover rounded-lg"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 truncate">{panel.mainText}</p>
                <p className="text-sm text-slate-500 truncate">{panel.smallText}</p>
              </div>
              <button
                type="button"
                onClick={() => handleDeleteAdPanel(panel.id)}
                className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
              >
                <FaTrash className="text-sm" />
              </button>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Testimonials Section */}
      <SectionCard icon={FaQuoteRight} title="Customer Testimonials" iconBg="bg-amber-50" iconColor="text-amber-600">
        <form onSubmit={handleTestimonialSubmit} className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Star Rating</label>
            <select
              value={testimonialForm.stars}
              onChange={(e) => setTestimonialForm({ ...testimonialForm, stars: parseInt(e.target.value) })}
              className={inputClass}
              required
            >
              <option value={5}>5 Stars</option>
              <option value={4}>4 Stars</option>
              <option value={3}>3 Stars</option>
              <option value={2}>2 Stars</option>
              <option value={1}>1 Star</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Review Description</label>
            <textarea
              value={testimonialForm.description}
              onChange={(e) => setTestimonialForm({ ...testimonialForm, description: e.target.value })}
              className={`${inputClass} resize-none`}
              rows="3"
              placeholder="Enter customer review"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Customer Name</label>
            <input
              type="text"
              value={testimonialForm.customerName}
              onChange={(e) => setTestimonialForm({ ...testimonialForm, customerName: e.target.value })}
              placeholder="e.g., Priya Sharma"
              required
              className={inputClass}
            />
          </div>
          <SubmitButton><FaPlus className="text-sm" /> Add Testimonial</SubmitButton>
        </form>

        <div className="space-y-3">
          <h3 className="text-sm font-medium text-slate-700">Current Testimonials ({testimonials.length})</h3>
          {testimonials.map((test) => (
            <div key={test.id} className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(test.stars)].map((_, i) => (
                      <FaStar key={i} className="text-amber-400 text-sm" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-2 text-sm leading-relaxed">{test.description}</p>
                  <p className="font-medium text-slate-800 text-sm">- {test.customerName}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteTestimonial(test.id)}
                  className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors ml-3"
                >
                  <FaTrash className="text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Transformations Section */}
      <SectionCard icon={FaExchangeAlt} title="Custom Order Transformations" iconBg="bg-emerald-50" iconColor="text-emerald-600">
        <form ref={transformationFormRef} onSubmit={handleTransformationSubmit} className="space-y-4 mb-6 scroll-mt-24">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Product Name</label>
            <input
              type="text"
              value={transformationForm.productName}
              onChange={(e) => setTransformationForm({ ...transformationForm, productName: e.target.value })}
              placeholder="e.g., Custom Baby Blanket"
              required
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Before (Customer Request)</label>
            <textarea
              value={transformationForm.before}
              onChange={(e) => setTransformationForm({ ...transformationForm, before: e.target.value })}
              className={`${inputClass} resize-none`}
              rows="2"
              placeholder="What the customer requested"
              required
            />
            {editingTransformationId && currentTransformationImages.before && !transformationForm.beforeImage && !transformationForm.removeBeforeImage && (
              <div className="mt-2 flex items-center gap-3">
                <img
                  src={`${process.env.REACT_APP_API_URL}/api/images/${currentTransformationImages.before}?w=200`}
                  alt="Current before"
                  className="w-12 h-12 object-cover rounded-md border border-slate-200"
                />
                <span className="text-xs text-slate-500">Current image</span>
                <button
                  type="button"
                  onClick={() => setTransformationForm({ ...transformationForm, removeBeforeImage: true })}
                  className="text-xs text-red-600 hover:underline"
                >
                  Remove
                </button>
              </div>
            )}
            {editingTransformationId && transformationForm.removeBeforeImage && (
              <p className="mt-2 text-xs text-red-600">Image will be removed on save. <button type="button" onClick={() => setTransformationForm({ ...transformationForm, removeBeforeImage: false })} className="underline">Undo</button></p>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setTransformationForm({ ...transformationForm, beforeImage: e.target.files[0] })}
              className="mt-2 block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
            <p className="text-xs text-slate-400 mt-1">{editingTransformationId ? 'Choose a file to replace the current image' : 'Optional image (shown as a small thumbnail, click to enlarge)'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">After (What We Delivered)</label>
            <textarea
              value={transformationForm.after}
              onChange={(e) => setTransformationForm({ ...transformationForm, after: e.target.value })}
              className={`${inputClass} resize-none`}
              rows="2"
              placeholder="What was delivered to the customer"
              required
            />
            {editingTransformationId && currentTransformationImages.after && !transformationForm.afterImage && !transformationForm.removeAfterImage && (
              <div className="mt-2 flex items-center gap-3">
                <img
                  src={`${process.env.REACT_APP_API_URL}/api/images/${currentTransformationImages.after}?w=200`}
                  alt="Current after"
                  className="w-12 h-12 object-cover rounded-md border border-slate-200"
                />
                <span className="text-xs text-slate-500">Current image</span>
                <button
                  type="button"
                  onClick={() => setTransformationForm({ ...transformationForm, removeAfterImage: true })}
                  className="text-xs text-red-600 hover:underline"
                >
                  Remove
                </button>
              </div>
            )}
            {editingTransformationId && transformationForm.removeAfterImage && (
              <p className="mt-2 text-xs text-red-600">Image will be removed on save. <button type="button" onClick={() => setTransformationForm({ ...transformationForm, removeAfterImage: false })} className="underline">Undo</button></p>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setTransformationForm({ ...transformationForm, afterImage: e.target.files[0] })}
              className="mt-2 block w-full text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
            <p className="text-xs text-slate-400 mt-1">{editingTransformationId ? 'Choose a file to replace the current image' : 'Optional image (shown as a small thumbnail, click to enlarge)'}</p>
          </div>
          <div className="flex items-center gap-3">
            <SubmitButton>
              {editingTransformationId ? <><FaPen className="text-sm" /> Update Transformation</> : <><FaPlus className="text-sm" /> Add Transformation</>}
            </SubmitButton>
            {editingTransformationId && (
              <button
                type="button"
                onClick={resetTransformationForm}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors text-sm font-medium"
              >
                <FaTimes className="text-sm" /> Cancel
              </button>
            )}
          </div>
        </form>

        <div className="space-y-3">
          <h3 className="text-sm font-medium text-slate-700">Current Transformations ({transformations.length})</h3>
          {transformations.map((trans) => (
            <div key={trans.id} className={`p-4 rounded-xl ${editingTransformationId === trans.id ? 'bg-emerald-50 ring-2 ring-emerald-300' : 'bg-slate-50'}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-800 mb-3">{trans.productName}</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-red-50 p-3 rounded-lg border border-red-100 flex items-start gap-3">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-red-600 mb-1">Before:</p>
                        <p className="text-sm text-slate-700">{trans.before}</p>
                      </div>
                      {trans.beforeImage && (
                        <img
                          src={`${process.env.REACT_APP_API_URL}/api/images/${trans.beforeImage}?w=200`}
                          alt="Before"
                          className="w-12 h-12 object-cover rounded-md border border-red-200 flex-shrink-0"
                        />
                      )}
                    </div>
                    <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-100 flex items-start gap-3">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-emerald-600 mb-1">After:</p>
                        <p className="text-sm text-slate-700">{trans.after}</p>
                      </div>
                      {trans.afterImage && (
                        <img
                          src={`${process.env.REACT_APP_API_URL}/api/images/${trans.afterImage}?w=200`}
                          alt="After"
                          className="w-12 h-12 object-cover rounded-md border border-emerald-200 flex-shrink-0"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 ml-3">
                  <button
                    type="button"
                    onClick={() => handleEditTransformation(trans)}
                    className="p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors"
                    aria-label="Edit transformation"
                  >
                    <FaPen className="text-sm" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteTransformation(trans.id)}
                    className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    aria-label="Delete transformation"
                  >
                    <FaTrash className="text-sm" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Categories Section */}
      <SectionCard icon={FaTags} title="Product Categories" iconBg="bg-blue-50" iconColor="text-blue-600">
        <form onSubmit={handleCategorySubmit} className="flex gap-3 mb-6">
          <input
            type="text"
            value={categoryForm.categoryName}
            onChange={(e) => setCategoryForm({ categoryName: e.target.value })}
            className={`flex-1 ${inputClass}`}
            placeholder="e.g., Beanies, Scarves, Custom"
            required
          />
          <button
            type="submit"
            className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-600 hover:to-primary-700 transition-all flex items-center gap-2"
          >
            <FaPlus className="text-sm" /> Add
          </button>
        </form>

        <div className="space-y-3">
          <h3 className="text-sm font-medium text-slate-700">Current Categories ({categories.length})</h3>
          {categories.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">No categories yet. Add your first category above!</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center gap-2 bg-slate-50 pl-4 pr-2 py-2 rounded-xl">
                  <span className="text-sm font-medium text-slate-700">{cat.name}</span>
                  <button
                    type="button"
                    onClick={() => handleDeleteCategory(cat.id)}
                    className="p-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <FaTrash className="text-xs" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>

      {/* Contact Details Section */}
      <SectionCard icon={FaPhone} title="Contact Details & Social Media" iconBg="bg-indigo-50" iconColor="text-indigo-600">
        <form onSubmit={handleContactDetailsSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                <FaPhone className="text-slate-400 text-xs" /> Phone Number
              </label>
              <input
                type="tel"
                value={contactDetails.phone}
                onChange={(e) => setContactDetails({ ...contactDetails, phone: e.target.value })}
                className={inputClass}
                placeholder="e.g., +91 9876543210"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                <FaEnvelope className="text-slate-400 text-xs" /> Email Address
              </label>
              <input
                type="email"
                value={contactDetails.email}
                onChange={(e) => setContactDetails({ ...contactDetails, email: e.target.value })}
                className={inputClass}
                placeholder="e.g., contact@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                <FaInstagram className="text-slate-400 text-xs" /> Instagram URL
              </label>
              <input
                type="url"
                value={contactDetails.instagram}
                onChange={(e) => setContactDetails({ ...contactDetails, instagram: e.target.value })}
                className={inputClass}
                placeholder="https://instagram.com/yourpage"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                <FaFacebook className="text-slate-400 text-xs" /> Facebook URL
              </label>
              <input
                type="url"
                value={contactDetails.facebook}
                onChange={(e) => setContactDetails({ ...contactDetails, facebook: e.target.value })}
                className={inputClass}
                placeholder="https://facebook.com/yourpage"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-2">
                <FaWhatsapp className="text-slate-400 text-xs" /> WhatsApp Link
              </label>
              <input
                type="url"
                value={contactDetails.whatsapp}
                onChange={(e) => setContactDetails({ ...contactDetails, whatsapp: e.target.value })}
                className={inputClass}
                placeholder="https://wa.me/919876543210"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                This link will be used for the "Customize" button and contact inquiries
              </p>
            </div>
          </div>
          <SubmitButton>Update Contact Details</SubmitButton>
        </form>
      </SectionCard>

      {/* Tax Details Section */}
      <SectionCard icon={FaPercent} title="Tax Details" iconBg="bg-rose-50" iconColor="text-rose-600">
        <form onSubmit={handleTaxDetailsSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">GST Rate (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={taxDetails.gstRate}
                onChange={(e) => setTaxDetails({ ...taxDetails, gstRate: e.target.value })}
                placeholder="e.g., 18"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">GST Number</label>
              <input
                type="text"
                value={taxDetails.gstNumber}
                onChange={(e) => setTaxDetails({ ...taxDetails, gstNumber: e.target.value })}
                placeholder="e.g., 22AAAAA0000A1Z5"
                className={inputClass}
              />
            </div>
          </div>
          <SubmitButton>Update Tax Details</SubmitButton>
        </form>
      </SectionCard>

      {/* Delivery Settings Section */}
      <SectionCard icon={FaTruck} title="Delivery Settings" iconBg="bg-cyan-50" iconColor="text-cyan-600">
        <form onSubmit={handleDeliverySettingsSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Free Delivery Threshold (INR)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={deliverySettings.freeDeliveryThreshold}
                onChange={(e) => setDeliverySettings({ ...deliverySettings, freeDeliveryThreshold: e.target.value })}
                className={inputClass}
                placeholder="e.g., 500"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                Orders above this amount get free delivery
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Delivery Charge (INR)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={deliverySettings.deliveryCharge}
                onChange={(e) => setDeliverySettings({ ...deliverySettings, deliveryCharge: e.target.value })}
                className={inputClass}
                placeholder="e.g., 50"
              />
              <p className="text-xs text-slate-500 mt-1.5">
                Charged for orders below the free delivery threshold
              </p>
            </div>
          </div>
          <SubmitButton>Update Delivery Settings</SubmitButton>
        </form>
      </SectionCard>
    </div>
  );
};

export default Settings;
